<template>
	<div class="costos_tab report_theme_container" data-theme="costos">
		<TabHeader
			title="Costos"
			description="Analiza la tendencia y composición de tus costos"
			:filterPeriod="filterPeriod"
			:filterOptions="visiblePeriodOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			calendar-mode="range"
			:date-range="calendarRange"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth"
			@update-date-range="updateDateRange">
			<template #center-sub-nav>
				<div class="sub_nav_filters">
					<button type="button"
						class="filter_btn"
						:class="{ 'is-active': isCuentasMode }"
						@click.stop="setSubFilter('cuentas')">
						Cuentas
					</button>
					<button v-for="category in categories"
						:key="category"
						type="button"
						class="filter_btn"
						:class="{ 'is-active': activeSubFilter === category }"
						@click.stop="setSubFilter(category)">
						{{ capitalize(category) }}
					</button>
					<!-- Tipos de contraparte: mismo estilo segmentado que el resto
						del control (como "Clientes" en Ventas), pero con su chevron
						de desplegable. Al presionarlo marca todos los tipos y abre
						el panel para deseleccionar. -->
					<MultiSelectDropdown
						segmented
						:value="selectedCounterpartTypes"
						:options="counterpartTypeOptions"
						placeholder="Contrapartes"
						select-all-on-open
						@input="SET_SELECTED_COUNTERPART_TYPES" />
				</div>
			</template>
		</TabHeader>

		<!-- Charts Section: gráficos del header (stacked + líneas por
			contraparte/cuenta), homologado con Gastos/Ventas. Colapsable, estado
			compartido por la storage-key de los gráficos. -->
		<CollapsibleSection class="mt-1"
			:title="chartsSectionTitle"
			chevron-right
			heading
			storage-key="financialReports:charts-expanded">
			<template #title-actions>
				<MultiSelectDropdown
					:value="selectedItems"
					:options="itemFilterOptions"
					:placeholder="itemFilterPlaceholder"
					:grouped="activeSubFilter !== 'contrapartes'"
					searchable
					align="right"
					@input="SET_SELECTED_ITEMS" />
			</template>
			<SalesChartsRow
				:charts-data="headerChartsData"
				:is-loading="headerChartsLoading"
				:error="headerChartsError"
				:percent-mode.sync="percentMode" />
		</CollapsibleSection>

		<!-- Main Table Section -->
		<ExpensesEvolutionTable class="mt-2"
			slim-header
			emptyMessage="No hay datos de costos para el período seleccionado."
			:monthHeaders="monthHeaders"
			:visibleRows="visibleRows"
			:totalRow="totalRow"
			:ratioRow="ratioRow"
			:expandedL2Rows="expandedL2Rows"
			:isLoading="isLoading"
			:error="error"
			:selectedPeriod="selectedPeriodRange"
			:periodOptions="periodOptions"
			:filterPeriod="filterPeriod"
			:period-select-enabled="false"
			:percent-mode="percentMode"
			:sales-by-period="salesByPeriod"
			:clickable-cells="true"
			:cell-tooltips="true"
			:comparison-enabled="true"
			:comparison="comparisonViewModel"
			@toggle-l2="toggleL2"
			@cell-click="openCostsAnalysis"
			@toggle-comparison="onToggleComparison"
			@comparison-add-year="onComparisonAddYear"
			@comparison-remove-year="onComparisonRemoveYear"
			@retry="fetchCostsData" />
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import CollapsibleSection from "@/components/financialReports/CollapsibleSection.vue";
import MultiSelectDropdown from "@/components/generics/MultiSelectDropdown.vue";
import SalesChartsRow from "@/components/financialReports/SalesChartsRow.vue";
import ExpensesEvolutionTable from "@/components/financialReports/ExpensesEvolutionTable.vue";
import ModalFullHeightRight from "@/components/modals/ModalFullHeightRight.vue";
import HeaderCostsAnalysis from "@/components/financialReports/costsAnalysis/HeaderCostsAnalysis.vue";
import BodyCostsAnalysis from "@/components/financialReports/costsAnalysis/BodyCostsAnalysis.vue";
import HeaderClientDetail from "@/components/financialReports/salesAnalysis/clientDetail/HeaderClientDetail.vue";
import BodyClientDetail from "@/components/financialReports/salesAnalysis/clientDetail/BodyClientDetail.vue";
import { obligationForType } from "@/components/financialReports/expensesAnalysis/counterpartObligation.js";
import {
	PERIOD_TYPES,
	COST_COUNTERPART_TYPES,
	FILTER_PERIOD_OPTIONS,
	TOP_CLIENTS_COUNT,
} from "@/config/reportStructure";
import { parseMonthLabel } from "../utils/periodFormatting";
import { capitalize } from "@/utils/string";

export default {
	name: "CostosTab",

	components: {
		TabHeader,
		CollapsibleSection,
		MultiSelectDropdown,
		SalesChartsRow,
		ExpensesEvolutionTable,
	},

	data() {
		return {
			periodOptions: PERIOD_TYPES,
			counterpartTypeOptions: COST_COUNTERPART_TYPES,
			expandedL2Rows: [],
			filterOptions: FILTER_PERIOD_OPTIONS,
			// "% de ventas": un solo toggle (en SalesChartsRow) que cambia el
			// chart de líneas y la tabla a porcentajes sobre ventas. Compartido.
			percentMode: false,
			// Panel de comparación abierto (toggle de la tabla). Lo trackeamos acá
			// para refetchear la comparación al cambiar el mes sólo si está visible.
			comparisonOpen: false,
		};
	},

	computed: {
		...mapState("classification", ["categories"]),
		...mapState("reportsFilters", ["endMonth", "selectedPeriodRange", "customRange", "pendingDrill"]),
		...mapState("financialReports", ["filterPeriod"]),
		...mapState("costsReport", [
			"activeSubFilter",
			"selectedCounterpartTypes",
			"selectedItems",
			"level2Accounts",
			"hierarchy",
			"counterpartTypes",
			"monthHeaders",
			"salesByPeriod",
			"isLoading",
			"error",
			"isChartsLoading",
			"chartsError",
			"comparisonData",
			"isComparisonLoading",
			"comparisonError",
			"comparisonYears",
		]),
		// Lente activo del modal de análisis — el header puede cambiar el mes;
		// retenemos account/level/month para el refetch.
		...mapState("costsAnalysis", {
			analysisAccount: "account",
			analysisAccountLevel: "accountLevel",
			analysisMonth: "month",
		}),
		...mapGetters("costsReport", [
			"tableRows", "totalRow", "ratioRow", "isCuentasMode", "headerChartsData",
		]),

		getNow() {
			return moment().toDate();
		},

		// Rango activo del calendar del header (modo range, igual que Gastos).
		calendarRange() {
			if (this.customRange) return this.customRange;
			const end = moment(this.endMonth).startOf("month");
			return { from: end.clone().subtract(11, "months").toDate(), to: end.toDate() };
		},

		// Título dinámico de la sección de gráficos según la dimensión activa.
		chartsSectionTitle() {
			if (this.isCuentasMode) return "Costos por cuenta";
			if (this.activeSubFilter === "contrapartes") return "Costos por contraparte";
			const singular = this.activeSubFilter.replace(/s$/i, "").toLowerCase();
			return `Costos por ${singular}`;
		},

		headerChartsLoading() {
			return this.isChartsLoading;
		},
		headerChartsError() {
			return this.chartsError || "";
		},

		// Opciones del filtro multi-select del header. En contrapartes es plano
		// (las mismas filas que la tabla); en cuentas/categoría agrupado por L2.
		itemFilterOptions() {
			if (this.activeSubFilter === "contrapartes") {
				return this.tableRows.map((row) => ({ id: row.label, label: row.label }));
			}
			const options = [];
			this.level2Accounts.forEach((l2) => {
				(this.hierarchy[l2] || []).forEach((l3) => {
					if (l3 === l2) return;
					options.push({ id: l3, label: l3, group: l2 });
				});
			});
			return options;
		},

		itemFilterPlaceholder() {
			if (this.isCuentasMode) return "Filtrar cuentas";
			if (this.activeSubFilter === "contrapartes") return "Filtrar contrapartes";
			return `Filtrar ${this.activeSubFilter.toLowerCase()}`;
		},

		// Períodos visibles en el header. YTD/Anual no aplican a la comparación,
		// así que con el panel abierto sólo dejamos Mensual y Trimestral.
		visiblePeriodOptions() {
			if (!this.comparisonOpen) return this.filterOptions;
			return this.filterOptions.filter((o) => o.id === "month" || o.id === "quarter");
		},

		// View-model que se pasa entero a ExpensesEvolutionTable → SalesComparisonChart.
		// Costos no tiene subcategorías → subItems vacío, selectedItem null.
		comparisonViewModel() {
			return {
				series: this.comparisonData ? this.comparisonData.series : [],
				months: this.comparisonData ? this.comparisonData.months : [],
				availableYears: this.comparisonData ? this.comparisonData.availableYears : [],
				isLoading: this.isComparisonLoading,
				error: this.comparisonError || "",
				subItems: [],
				selectedItem: null,
				years: this.comparisonYears,
				period: this.filterPeriod,
			};
		},

		visibleRows() {
			// Contrapartes: tabla plana (una fila por contraparte, sin anidar ni
			// expandir/colapsar). El filtro de items, si hay, acota la lista.
			if (this.activeSubFilter === "contrapartes") {
				if (!this.selectedItems.length) return this.tableRows;
				const selected = new Set(this.selectedItems);
				return this.tableRows.filter((row) => selected.has(row.label));
			}
			// Sin filtro de items: L2 siempre visibles, L3 según la expansión.
			if (!this.selectedItems.length) {
				return this.tableRows.filter((row) => {
					if (row.level === 2) return true;
					return this.expandedL2Rows.includes(row.parent);
				});
			}
			// Con filtro: sólo las L3 seleccionadas (dentro de su L2 expandida) y
			// las L2 que tengan al menos una L3 seleccionada.
			const selected = new Set(this.selectedItems);
			const l2WithSelected = new Set(
				this.tableRows
					.filter((row) => row.level === 3 && selected.has(row.label))
					.map((row) => row.parent),
			);
			return this.tableRows.filter((row) => {
				if (row.level === 2) return l2WithSelected.has(row.label);
				return selected.has(row.label) && this.expandedL2Rows.includes(row.parent);
			});
		},

		// Firma reactiva consolidada — un solo trigger para fetch aunque varios
		// filtros cambien en la misma transacción.
		fetchSignature() {
			return [
				this.endMonth,
				this.selectedPeriodRange,
				this.activeSubFilter,
				this.selectedCounterpartTypes.join(","),
				this.filterPeriod,
				this.customRange && this.customRange.from,
				this.customRange && this.customRange.to,
			].join("|");
		},
	},

	methods: {
		...mapActions("costsReport", [
			"fetchCostsData", "fetchCostsCharts", "fetchCostsComparison",
		]),
		...mapActions("costsAnalysis", ["fetchCostsAnalysis"]),
		// Drawer de detalle de contraparte (modal 2). Mismo módulo que usa
		// BodyCostsAnalysis al clickear una contraparte del ranking.
		...mapActions("clientDetail", {
			openClientDetail: "openClientDetail",
			closeClientDetail: "closeClientDetail",
			changeClientRangePeriod: "changeRangePeriod",
		}),
		...mapActions("reportsFilters", ["setCustomRange"]),
		// Categorías de clasificación (tiendas/marcas/…) son globales; la action
		// hace short-circuit si ya las cargó otro view.
		...mapActions("classification", ["getCategories"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH", "SET_PENDING_DRILL"]),
		...mapMutations("costsReport", [
			"SET_ACTIVE_SUB_FILTER",
			"SET_SELECTED_COUNTERPART_TYPES",
			"SET_SELECTED_ITEMS",
			"SET_COMPARISON_YEARS",
		]),
		capitalize,
		...mapMutations("costsAnalysis", {
			resetCostsAnalysis: "RESET",
		}),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),

		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
		// El calendar del header (modo range) emite el rango; la action sincroniza
		// customRange + selectedPeriodRange="custom" + endMonth.
		updateDateRange(range) {
			this.setCustomRange(range);
		},
		// Cambia la dimensión del header (cuentas / categoría). Contrapartes se
		// activa por su propio dropdown (SET_SELECTED_COUNTERPART_TYPES), no acá.
		setSubFilter(filter) {
			this.SET_ACTIVE_SUB_FILTER(filter);
		},
		toggleL2(label) {
			const index = this.expandedL2Rows.indexOf(label);
			if (index > -1) {
				this.expandedL2Rows.splice(index, 1);
			} else {
				this.expandedL2Rows.push(label);
			}
		},

		// --- Comparación año-contra-año ---
		onToggleComparison(open) {
			this.comparisonOpen = open;
			if (!open) return;
			if (this.filterPeriod !== "month" && this.filterPeriod !== "quarter") {
				this.SET_FILTER_PERIOD("month");
			}
			this.fetchCostsComparison();
		},
		onComparisonAddYear(year) {
			this.SET_COMPARISON_YEARS([...this.comparisonYears, year]);
			this.fetchCostsComparison();
		},
		onComparisonRemoveYear(year) {
			this.SET_COMPARISON_YEARS(
				this.comparisonYears.filter((y) => y !== year),
			);
			this.fetchCostsComparison();
		},

		// --- Modal de Análisis de Costos -------------------------------------

		// Cell-click de la tabla → abre el drawer de análisis del mes con la
		// cuenta clickeada pre-seleccionada en la card del estado (card 1), que
		// recorta el ranking de contrapartes a esa cuenta. Una fila L2 (grupo)
		// es siempre una cuenta del plan; una L3 sólo lo es en modo cuentas (en
		// categorías la L3 es un atributo y en contrapartes la fila es una
		// contraparte) → en esos casos, y en la fila Total, arranca con todas.
		openCostsAnalysis({ monthLabel, account, level }) {
			const month = parseMonthLabel(monthLabel);
			if (!month) return;

			// En contrapartes la tabla lista contrapartes: el clic en una fila va
			// directo al detalle de la contraparte (modal 2), saltando el análisis
			// del mes (modal 1). La fila Total (sin account) cae al análisis general.
			if (this.activeSubFilter === "contrapartes" && account) {
				this.openCounterpartDetailFromCell({
					counterpart: account,
					month,
					counterpartType: this.counterpartTypes[account] || null,
				});
				return;
			}

			let drillAccount = null;
			let drillLevel = null;
			if (level === 2) {
				drillAccount = account;
				drillLevel = "2";
			} else if (level === 3 && this.isCuentasMode) {
				drillAccount = account;
				drillLevel = "3";
			}

			const monthDate = moment(month, "YYYY-MM").toDate();
			const periodLabel = moment(month, "YYYY-MM")
				.locale("es")
				.format("MMM YYYY")
				.replace(/^./, (c) => c.toUpperCase());

			this.fetchCostsAnalysis({ month, account: drillAccount, accountLevel: drillLevel, groupBy: null, selectedItems: {} });
			this.$buefy.modal.open({
				parent: this,
				hasModalCard: true,
				customClass: "wide-summary-modal",
				canCancel: ["escape", "outside"],
				component: ModalFullHeightRight,
				props: {
					headerComp: HeaderCostsAnalysis,
					bodyComp: BodyCostsAnalysis,
					headerProps: {
						periodLabel,
						endMonth: monthDate,
						getNow: this.getNow,
					},
					bodyProps: null,
					// El modal se monta en document.body — sin esto el subárbol no
					// hereda el [data-theme="costos"] del .costos_tab.
					theme: "costos",
				},
				events: {
					closed: this.onAnalysisClose,
					"update-end-month": this.onAnalysisMonthChange,
				},
			});
		},

		// Atajo de contrapartes: abre directo el drawer de detalle de la
		// contraparte clickeada en la tabla (modal 2). El tipo viene del breakdown
		// (counterpartTypes) → obligación + filtrado correctos.
		openCounterpartDetailFromCell({ counterpart, month, counterpartType }) {
			this.openClientDetail({
				alias: counterpart,
				documentType: "P",
				monthIso: month,
				obligationName: obligationForType(counterpartType),
				level1Name: "Costos",
				counterpartType,
			});
			this.$buefy.modal.open({
				parent: this,
				hasModalCard: true,
				customClass: "wide-summary-modal",
				canCancel: ["escape", "outside"],
				component: ModalFullHeightRight,
				props: {
					headerComp: HeaderClientDetail,
					bodyComp: BodyClientDetail,
					headerProps: { fallbackAlias: counterpart },
					bodyProps: null,
					// Primer drawer del flujo (no apilado) → ancho default 85%.
					theme: "costos",
					width: "85%",
				},
				events: {
					closed: this.closeClientDetail,
					"update-range-period": this.changeClientRangePeriod,
				},
			});
		},

		// El usuario cambió el mes desde el AnualCalendar del header del drawer.
		// account/level se conservan — sólo cambia el período.
		onAnalysisMonthChange(date) {
			const month = moment(date).format("YYYY-MM");
			this.fetchCostsAnalysis({
				month,
				account: this.analysisAccount,
				accountLevel: this.analysisAccountLevel,
				groupBy: null,
				selectedItems: {},
			});
		},

		// Cierre del drawer: resetea el lente de análisis.
		onAnalysisClose() {
			this.resetCostsAnalysis();
		},

		// Consume el pendingDrill (seteado por ResultadosTab al clickear una
		// celda de costos en el P&L). Abre el modal del mes pedido para el costo
		// completo (account null).
		async consumePendingDrill() {
			const drill = this.pendingDrill;
			if (!drill || drill.tab !== "costos") return;
			this.SET_PENDING_DRILL(null);
			await this.getCategories();
			this.openCostsAnalysis({ monthLabel: drill.monthLabel, account: null, level: null });
		},
	},

	mounted() {
		this.fetchCostsData();
		this.fetchCostsCharts({ topN: TOP_CLIENTS_COUNT });
		// Alimenta los chips de categoría del header (short-circuit si ya
		// cargaron por otro view).
		this.getCategories();
		// Drill cross-tab: si llegó clickeando una celda de costos en el P&L.
		this.consumePendingDrill();
	},

	watch: {
		// Tabla y gráficos comparten los mismos filtros: un solo trigger refetchea
		// ambos.
		fetchSignature() {
			this.fetchCostsData();
			this.fetchCostsCharts({ topN: TOP_CLIENTS_COUNT });
		},
		// Por default todas las cuentas L2 arrancan desplegadas tras cada fetch.
		tableRows: {
			immediate: true,
			handler(rows) {
				this.expandedL2Rows = rows
					.filter((row) => row.level === 2)
					.map((row) => row.label);
			},
		},
		// El mes del header redefine el cutoff del año en curso de la comparación
		// — refetch sólo si el panel está abierto.
		endMonth() {
			if (this.comparisonOpen) this.fetchCostsComparison();
		},
	},
};
</script>

<style scoped lang="scss">
.costos_tab {
	position: relative;
	padding-bottom: 40px;
	height: 100%;
}

// El header común vive en TabHeader.vue. Acá solo queda el SCSS del control
// segmentado del slot #center-sub-nav (Cuentas + un chip por categoría + el
// dropdown de tipos de contraparte, todos dentro de .sub_nav_filters).
// Replicamos .sub_nav_filters/.filter_btn localmente porque TabHeader es
// scoped y sus estilos no llegan al markup inyectado al slot.

.sub_nav_filters {
	display: inline-flex;
	align-items: center;
	gap: 2px;
	padding: 0 2px;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
	box-sizing: border-box;
	min-height: 34px;
}

.filter_btn {
	display: inline-flex;
	align-items: center;
	padding: 0 16px;
	min-height: 32px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	line-height: 1;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.15s ease;
	box-sizing: border-box;

	&:hover { background: var(--report-surface); }

	&.is-active {
		background: var(--report-primary);
		color: var(--report-bg);
	}
}
</style>
